import { RelodeApiError } from "@relode/core";
import {
    ActionsUnion,
    createAction,
} from "@relode/core/components/reducer/actionHelpers";

import { JobManagerContact, DashboardType } from "../../../shared/types";
import { ManagerStatTypes, MemberStatTypes } from "./dashboardState";
import { MemberUpcomingInterview } from "./types/memberUpcomingInterview";
import { DashboardJobStatistics } from "./types";

enum names {
    LOAD_JOB_MANAGER_DETAILS = "LOAD_JOB_MANAGER_DETAILS",
    LOAD_JOB_MANAGER_DETAILS_SUCCESS = "LOAD_JOB_MANAGER_DETAILS_SUCCESS",
    LOAD_JOB_MANAGER_DETAILS_FAILURE = "LOAD_JOB_MANAGER_DETAILS_FAILURE",
    LOAD_DASHBOARD_TYPE = "LOAD_DASHBOARD_TYPE",
    LOAD_DASHBOARD_TYPE_SUCCESS = "LOAD_DASHBOARD_TYPE_SUCCESS",
    LOAD_DASHBOARD_TYPE_FAILURE = "LOAD_DASHBOARD_TYPE_FAILURE",
    LOAD_MANAGER_DASHBOARD_COUNT = "LOAD_MANAGER_DASHBOARD_COUNT",
    LOAD_MANAGER_DASHBOARD_COUNT_SUCCESS = "LOAD_MANAGER_DASHBOARD_COUNT_SUCCESS",
    LOAD_MANAGER_DASHBOARD_COUNT_FAILURE = "LOAD_MANAGER_DASHBOARD_COUNT_FAILURE",
    LOAD_MANAGER_JOB_STATISTICS = "LOAD_MANAGER_JOB_STATISTICS",
    LOAD_MANAGER_JOB_STATISTICS_SUCCESS = "LOAD_MANAGER_JOB_STATISTICS_SUCCESS",
    LOAD_MANAGER_JOB_STATISTICS_FAILURE = "LOAD_MANAGER_JOB_STATISTICS_FAILURE",
    LOAD_MEMBER_DASHBOARD_COUNT = "LOAD_MEMBER_DASHBOARD_COUNT",
    LOAD_MEMBER_DASHBOARD_COUNT_SUCCESS = "LOAD_MEMBER_DASHBOARD_COUNT_SUCCESS",
    LOAD_MEMBER_DASHBOARD_COUNT_FAILURE = "LOAD_MEMBER_DASHBOARD_COUNT_FAILURE",
    LOAD_MEMBER_UPCOMING_INTERVIEWS = "LOAD_MEMBER_UPCOMING_INTERVIEWS",
    LOAD_MEMBER_UPCOMING_INTERVIEWS_SUCCESS = "LOAD_MEMBER_UPCOMING_INTERVIEWS_SUCCESS",
    LOAD_MEMBER_UPCOMING_INTERVIEWS_FAILURE = "LOAD_MEMBER_UPCOMING_INTERVIEWS_FAILURE",
    LOAD_MANAGER_JOB_COUNTS = "LOAD_MANAGER_JOB_COUNTS",
    LOAD_MANAGER_JOB_COUNTS_SUCCESS = "LOAD_MANAGER_JOB_COUNTS_SUCCESS",
    LOAD_MANAGER_JOB_COUNTS_FAILURE = "LOAD_MANAGER_JOB_COUNTS_FAILURE",
    LOAD_MEMBER_JOB_COUNTS = "LOAD_MEMBER_JOB_COUNTS",
    LOAD_MEMBER_JOB_COUNTS_SUCCESS = "LOAD_MEMBER_JOB_COUNTS_SUCCESS",
    LOAD_MEMBER_JOB_COUNTS_FAILURE = "LOAD_MEMBER_JOB_COUNTS_FAILURE",
}

export default names;

export const creators = {
    // ADMIN details
    loadJobManagerDetails: () => createAction(names.LOAD_JOB_MANAGER_DETAILS),
    loadJobManagerDetailsSuccess: (jobManagerContact: JobManagerContact) =>
        createAction(names.LOAD_JOB_MANAGER_DETAILS_SUCCESS, {
            jobManagerContact,
        }),
    loadJobManagerDetailsFailure: (apiError: RelodeApiError) =>
        createAction(names.LOAD_JOB_MANAGER_DETAILS_FAILURE, {
            apiError,
        }),

    // dashboard type
    loadDashboardType: (showAll: boolean) =>
        createAction(names.LOAD_DASHBOARD_TYPE, { showAll }),
    loadDashboardTypeSuccess: (dashboardType: DashboardType) =>
        createAction(names.LOAD_DASHBOARD_TYPE_SUCCESS, {
            dashboardType,
        }),
    loadDashboardTypeFailure: (error: RelodeApiError) =>
        createAction(names.LOAD_DASHBOARD_TYPE_FAILURE, { error }),

    //manager
    loadManagerDashboardCount: (type: ManagerStatTypes) =>
        createAction(names.LOAD_MANAGER_DASHBOARD_COUNT, { type }),
    loadManagerDashboardCountSuccess: (type: ManagerStatTypes, count: number) =>
        createAction(names.LOAD_MANAGER_DASHBOARD_COUNT_SUCCESS, {
            type,
            count,
        }),
    loadManagerDashboardCountFailure: (
        type: ManagerStatTypes,
        error: RelodeApiError
    ) =>
        createAction(names.LOAD_MANAGER_DASHBOARD_COUNT_FAILURE, {
            type,
            error,
        }),

    loadManagerJobStatistics: () =>
        createAction(names.LOAD_MANAGER_JOB_STATISTICS),
    loadManagerJobStatisticsSuccess: (stats: DashboardJobStatistics) =>
        createAction(names.LOAD_MANAGER_JOB_STATISTICS_SUCCESS, {
            stats,
        }),
    loadManagerJobStatisticsFailure: (error: RelodeApiError) =>
        createAction(names.LOAD_MANAGER_JOB_STATISTICS_FAILURE, {
            error,
        }),

    loadManagerJobCounts: () => createAction(names.LOAD_MANAGER_JOB_COUNTS),
    loadManagerJobCountsSuccess: (counts: {
        open: number;
        closed: number;
        paused: number;
        pending: number;
        requested: number;
    }) => createAction(names.LOAD_MANAGER_JOB_COUNTS_SUCCESS, { counts }),
    loadManagerJobCountsFailure: (error: RelodeApiError) =>
        createAction(names.LOAD_MANAGER_JOB_COUNTS_FAILURE, { error }),

    // member
    loadHiringTeamMemberDashboardCount: (type: MemberStatTypes) =>
        createAction(names.LOAD_MEMBER_DASHBOARD_COUNT, { type }),
    loadMemberDashboardCountSuccess: (type: MemberStatTypes, count: number) =>
        createAction(names.LOAD_MEMBER_DASHBOARD_COUNT_SUCCESS, {
            type,
            count,
        }),
    loadMemberDashboardCountFailure: (
        type: MemberStatTypes,
        error: RelodeApiError
    ) =>
        createAction(names.LOAD_MEMBER_DASHBOARD_COUNT_FAILURE, {
            type,
            error,
        }),

    loadMemberUpcomingInterviews: () =>
        createAction(names.LOAD_MEMBER_UPCOMING_INTERVIEWS),
    loadMemberUpcomingInterviewsSuccess: (
        interviews: MemberUpcomingInterview[]
    ) =>
        createAction(names.LOAD_MEMBER_UPCOMING_INTERVIEWS_SUCCESS, {
            interviews,
        }),
    loadMemberUpcomingInterviewsFailure: (error: RelodeApiError) =>
        createAction(names.LOAD_MEMBER_UPCOMING_INTERVIEWS_FAILURE, {
            error,
        }),
    loadMemberJobCounts: () => createAction(names.LOAD_MEMBER_JOB_COUNTS),
    loadMemberJobCountsSuccess: (counts: {
        open: number;
        closed: number;
        paused: number;
    }) => createAction(names.LOAD_MEMBER_JOB_COUNTS_SUCCESS, { counts }),
    loadMemberJobCountsFailure: (error: RelodeApiError) =>
        createAction(names.LOAD_MEMBER_JOB_COUNTS_FAILURE, { error }),
};

export type DashboardActionTypes = ActionsUnion<typeof creators>;
